import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Leaf, Target, Eye, Sparkles, Users, Mail, Phone, MapPin, Globe,
  Sun, Droplets, Play, Pause, Loader2, ArrowRight, Check, Handshake, Send, CheckCircle2, X, Volume2, VolumeX, Music, Music2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const enqInput = "w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 text-sm outline-none focus:border-white/30 transition-colors";

const THEMES = {
  emerald: { text: "text-emerald-400", bg: "bg-emerald-500", bgHover: "hover:bg-emerald-400", btnText: "text-slate-900",
    soft: "bg-emerald-500/15 text-emerald-400", border: "hover:border-emerald-400/40",
    chip: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", grad: "from-emerald-600/15 to-indigo-600/10",
    ring: "from-emerald-400 to-indigo-500", logo: "bg-emerald-500" },
  indigo: { text: "text-indigo-300", bg: "bg-indigo-500", bgHover: "hover:bg-indigo-400", btnText: "text-white",
    soft: "bg-indigo-500/15 text-indigo-300", border: "hover:border-indigo-400/40",
    chip: "bg-indigo-500/10 border-indigo-500/30 text-indigo-200", grad: "from-indigo-600/20 to-fuchsia-600/10",
    ring: "from-indigo-400 to-fuchsia-500", logo: "bg-indigo-500" },
  amber: { text: "text-amber-300", bg: "bg-amber-500", bgHover: "hover:bg-amber-400", btnText: "text-slate-900",
    soft: "bg-amber-500/15 text-amber-300", border: "hover:border-amber-400/40",
    chip: "bg-amber-500/10 border-amber-500/30 text-amber-200", grad: "from-amber-500/15 to-rose-600/10",
    ring: "from-amber-400 to-rose-500", logo: "bg-amber-500" },
};

export default function PublicProfile() {
  const { companyId } = useParams();
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    axios.get(`${API}/public/company-profile/${companyId}`)
      .then(({ data }) => {
        setD(data); setStatus("ok");
        document.title = "FInfraTech Services";
      })
      .catch(() => setStatus("error"));
  }, [companyId]);

  if (status === "loading") return <div className="min-h-screen grid place-items-center bg-slate-950"><Loader2 className="animate-spin text-emerald-400" size={32} /></div>;
  if (status === "error") return <div className="min-h-screen grid place-items-center bg-slate-950 text-slate-300">Profile not found.</div>;

  const { company, site } = d;
  const t = THEMES[site.theme] || THEMES.emerald;
  const gallery = site.aim_project?.gallery || [];
  const sections = site.sections || [];
  const navItems = [
    ["intro", "Introduction"], ["profile", "Company"], ["aim", "Aim Project"],
    ["benefits", "Benefits"], ["comparison", "Compare"], ["income", "Income"],
    ["investment", "Investment"], ["contact", "Contact"],
  ];
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden" data-testid="public-profile">
      <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {company.logo_url ? <img src={fileUrl(company.logo_url)} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/20" /> : <div className={`w-8 h-8 rounded-lg ${t.logo} grid place-items-center`}><Leaf size={17} className="text-slate-900" /></div>}
          </div>
          <nav className="hidden md:flex items-center gap-0.5">
            {navItems.map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} className={`px-2.5 py-1.5 text-[13px] text-slate-300 hover:${t.text} transition-colors whitespace-nowrap`}>{label}</button>
            ))}
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        {(site.hero_media || []).length > 0 ? (
          <HeroCinematic media={site.hero_media} t={t} />
        ) : (
          <>
            <div className={`absolute -top-24 -right-24 w-96 h-96 rounded-full ${t.bg}/20 blur-3xl`} />
            <div className="absolute top-40 -left-24 w-96 h-96 rounded-full bg-indigo-500/20 blur-3xl" />
          </>
        )}
        <div className="relative z-10 max-w-6xl w-full mx-auto px-5 py-20 md:py-28">
          {company.logo_url && (
            <img src={fileUrl(company.logo_url)} alt="" data-testid="hero-logo"
              className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover ring-4 ring-white/15 shadow-2xl shadow-emerald-500/30 mb-7 animate-fade-up" />
          )}
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${t.chip} text-xs font-semibold mb-5 animate-fade-up`} style={{ animationDelay: "80ms" }}>
            <Sparkles size={13} /> Clean Energy · Sustainable Agriculture · Water Security
          </div>
          <h1 className="font-heading font-black tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)] animate-fade-up" style={{ animationDelay: "140ms" }} data-testid="hero-company-name">
            FInfraTech <span className={t.text}>Services</span>
          </h1>
          <h2 className="mt-5 text-xl sm:text-2xl lg:text-3xl font-heading font-bold tracking-tight leading-tight bg-gradient-to-br from-white via-white to-emerald-300 bg-clip-text text-transparent max-w-3xl animate-fade-up" style={{ animationDelay: "220ms" }} data-testid="hero-name">
            {site.hero_tagline}
          </h2>
          <div className="mt-8 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "300ms" }}>
            <button onClick={() => scrollTo("aim")} className={`${t.bg} ${t.bgHover} ${t.btnText} font-bold px-6 py-3 rounded-xl flex items-center gap-2 transition-colors`}>Explore our aim project <ArrowRight size={18} /></button>
            <button onClick={() => scrollTo("contact")} className="border border-white/20 hover:bg-white/5 font-semibold px-6 py-3 rounded-xl transition-colors backdrop-blur-sm">Get in touch</button>
          </div>
        </div>
      </section>
      <div id="intro" />

      <Section id="mv" t={t} title="Our Mission & Vision" kicker="Why we exist">
        <div className="grid md:grid-cols-2 gap-5">
          <Card t={t} icon={Target} title="Mission">{site.mission}</Card>
          <Card t={t} icon={Eye} title="Vision">{site.vision}</Card>
        </div>
      </Section>

      <Section id="expertise" t={t} title="Key Areas of Expertise" kicker="What we do best">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(site.expertise || []).map((e, i) => (
            <div key={i} className={`rounded-2xl bg-white/5 border border-white/10 p-5 ${t.border} transition-colors`}>
              <div className={`w-10 h-10 rounded-xl ${t.soft} grid place-items-center mb-3`}>{[<Sun size={18} />, <Droplets size={18} />, <Leaf size={18} />, <Sparkles size={18} />][i % 4]}</div>
              <h3 className="font-bold">{e.title}</h3>
              <p className="text-sm text-slate-400 mt-1">{e.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="profile" t={t} title="Company Profile" kicker="Who we are">
        <p className="text-slate-300 leading-relaxed max-w-3xl mb-6">{site.company_profile}</p>
        {site.video_url ? (
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video">
            <video src={fileUrl(site.video_url)} controls className="w-full h-full object-cover" data-testid="profile-video" />
          </div>
        ) : site.solution_reel?.length ? (
          <SolutionReel slides={site.solution_reel} t={t} />
        ) : (
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video grid place-items-center">
            {gallery[0] ? <img src={fileUrl(typeof gallery[0] === "string" ? gallery[0] : gallery[0].url)} alt="" className="w-full h-full object-cover" />
              : <div className="text-center text-slate-500"><Play size={30} className="mx-auto mb-2" /><p className="text-sm">Company video coming soon</p></div>}
          </div>
        )}
      </Section>

      <section id="aim" className="relative py-16 md:py-24">
        <div className={`absolute inset-0 bg-gradient-to-br ${t.grad}`} />
        <div className="relative max-w-6xl mx-auto px-5">
          <p className={`${t.text} font-semibold text-sm uppercase tracking-widest`}>Our flagship initiative</p>
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold mt-2 max-w-2xl">{site.aim_project?.title}</h2>
          <p className="mt-4 text-slate-300 max-w-3xl text-lg">{site.aim_project?.description}</p>
          {gallery.length > 0 && (
            <div className="mt-8" data-testid="aim-reel">
              <HighlightReel items={gallery} t={t} title="Aim Project" />
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {(site.aim_project?.highlights || []).map((h, i) => (
              <div key={i} className="rounded-2xl bg-slate-900/60 border border-white/10 p-5">
                <div className={`w-9 h-9 rounded-lg ${t.bg} ${t.btnText} grid place-items-center font-bold mb-3`}>{i + 1}</div>
                <p className="text-sm text-slate-200">{h}</p>
              </div>
            ))}
          </div>
          {gallery.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="aim-gallery">
              {gallery.map((g, i) => {
                const url = typeof g === "string" ? g : g?.url || "";
                const caption = typeof g === "string" ? "" : g?.caption || "";
                return (
                  <figure key={i} data-testid={`gallery-item-${i}`}>
                    <div className="group relative overflow-hidden rounded-xl border border-white/10">
                      <img src={fileUrl(url)} alt={caption} className="h-32 w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      {caption && (
                        <figcaption className="absolute inset-x-0 bottom-0 translate-y-full opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 bg-gradient-to-t from-slate-950/95 to-slate-950/10 p-3 text-xs text-slate-100" data-testid={`gallery-caption-hover-${i}`}>
                          {caption}
                        </figcaption>
                      )}
                    </div>
                    {caption && (
                      <p className="mt-1.5 px-0.5 text-xs text-slate-400 leading-snug" data-testid={`gallery-caption-label-${i}`}>{caption}</p>
                    )}
                  </figure>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {sections.map((s) => <DynSection key={s.id} s={s} t={t} />)}

      <Section id="contact" t={t} title="Contact Us" kicker="Let's build something green">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            site.contact?.email && { icon: Mail, label: "Email", value: site.contact.email, href: `mailto:${site.contact.email}` },
            (site.contact?.website || company.website) && { icon: Globe, label: "Website", value: site.contact?.website || company.website, href: site.contact?.website_url || `https://${(site.contact?.website || company.website).replace(/^https?:\/\//, "")}` },
            site.contact?.phone && { icon: Phone, label: "Phone", value: site.contact.phone, href: `tel:${site.contact.phone}` },
            (site.contact?.address || company.address) && { icon: MapPin, label: "Address", value: site.contact?.address || company.address },
          ].filter(Boolean).map((c) => (
            <ContactCard key={c.label} t={t} icon={c.icon} label={c.label} value={c.value} href={c.href} />
          ))}
        </div>
      </Section>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-slate-500">© {new Date().getFullYear()} · Crafted with SyncPe</footer>

      <EnquiryWidget companyId={company.slug || companyId} companyName="us" t={t} />
    </div>
  );
}

function EnquiryWidget({ companyId, companyName, t }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", company_website: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const close = () => { setOpen(false); setTimeout(() => { setDone(false); setForm({ name: "", email: "", phone: "", message: "", company_website: "" }); }, 300); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/public/company-profile/${companyId}/enquiry`, form);
      setDone(true);
    } catch {
      alert("Sorry, we couldn't send your enquiry. Please try again.");
    } finally { setSending(false); }
  };
  return (
    <>
      <button data-testid="enquiry-fab" onClick={() => setOpen(true)}
        className={`fixed bottom-5 right-5 z-50 ${t.bg} ${t.bgHover} ${t.btnText} font-bold px-5 py-3.5 rounded-full shadow-2xl flex items-center gap-2 transition-transform hover:scale-105`}>
        <Handshake size={18} /> Partner / Invest
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-slate-950/70 backdrop-blur-sm p-4" data-testid="enquiry-modal">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md p-6 relative animate-fade-up">
            <button onClick={close} data-testid="enquiry-close" className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
            {done ? (
              <div className="text-center py-6" data-testid="enquiry-success">
                <div className={`w-14 h-14 rounded-full ${t.soft} grid place-items-center mx-auto mb-4`}><CheckCircle2 size={28} /></div>
                <h3 className="text-xl font-heading font-bold">Thank you!</h3>
                <p className="text-slate-400 mt-2 text-sm">Your enquiry has been sent to the {companyName} team. We'll be in touch shortly.</p>
                <button onClick={close} className={`mt-5 ${t.bg} ${t.bgHover} ${t.btnText} font-semibold px-5 py-2.5 rounded-xl transition-colors`}>Done</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <h3 className="text-xl font-heading font-bold">Partner / Invest with us</h3>
                  <p className="text-slate-400 text-sm mt-1">Share your details and our team will reach out.</p>
                </div>
                <input type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                  value={form.company_website} onChange={(e) => set("company_website", e.target.value)}
                  className="hidden" data-testid="enquiry-hp" />
                <input data-testid="enquiry-name" required placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} className={enqInput} />
                <input data-testid="enquiry-email" required type="email" placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} className={enqInput} />
                <input data-testid="enquiry-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={enqInput} />
                <textarea data-testid="enquiry-message" rows={3} placeholder="How would you like to partner or invest?" value={form.message} onChange={(e) => set("message", e.target.value)} className={enqInput} />
                <button type="submit" disabled={sending} data-testid="enquiry-submit"
                  className={`w-full ${t.bg} ${t.bgHover} ${t.btnText} font-bold px-5 py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 transition-colors`}>
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />} Send enquiry
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function HeroCinematic({ media, t }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (media.length < 2) return;
    const id = setTimeout(() => setI((x) => (x + 1) % media.length), 5500);
    return () => clearTimeout(id);
  }, [i, media.length]);
  return (
    <div className="absolute inset-0 z-0" data-testid="hero-cinematic">
      {media.map((url, idx) => (
        <div key={idx} className="absolute inset-0 overflow-hidden"
          style={{ opacity: idx === i ? 1 : 0, transition: "opacity 1400ms ease-in-out" }}>
          <img src={fileUrl(url)} alt="" className={`w-full h-full object-cover ${idx === i ? "hero-kb" : ""}`} key={`${idx}-${idx === i ? i : "x"}`} />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/45 to-slate-950/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/20 to-transparent" />
      <div className={`absolute -top-24 -right-16 w-[38rem] h-[38rem] rounded-full ${t.bg}/25 blur-3xl hero-glow`} />
      <div className="absolute -bottom-32 left-0 w-[34rem] h-[34rem] rounded-full bg-indigo-500/20 blur-3xl hero-glow" style={{ animationDelay: "2.5s" }} />
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.06]">
        <div className="hero-scan w-full h-1/3 bg-gradient-to-b from-transparent via-white to-transparent" />
      </div>
    </div>
  );
}

function HighlightReel({ items, t, title }) {
  const slides = items.map((g) => (typeof g === "string" ? { url: g, caption: "" } : { url: g?.url || "", caption: g?.caption || "" }));
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [narrate, setNarrate] = useState(false);
  const [music, setMusic] = useState(false);
  const audioRef = useRef(null);
  const DUR = 6000;

  useEffect(() => {
    if (!playing || slides.length < 2) return;
    const id = setTimeout(() => setI((x) => (x + 1) % slides.length), DUR);
    return () => clearTimeout(id);
  }, [playing, slides.length, i]);

  // Web Speech narration of the current caption (client-side, no keys).
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const cap = slides[i]?.caption;
    if (narrate && playing && cap) {
      const u = new SpeechSynthesisUtterance(cap);
      u.rate = 0.98; u.pitch = 1;
      window.speechSynthesis.speak(u);
    }
    return () => window.speechSynthesis && window.speechSynthesis.cancel();
  }, [i, narrate, playing]);

  useEffect(() => () => { if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel(); }, []);

  // Optional ambient soundtrack — synthesized locally (no external asset / keys).
  useEffect(() => {
    if (!music || typeof window === "undefined") {
      if (audioRef.current) { try { audioRef.current.stop(); } catch (e) {} audioRef.current = null; }
      return;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 0; master.connect(ctx.destination);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass"; filter.frequency.value = 700; filter.connect(master);
    const oscs = [110, 164.81, 220].map((f) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f;
      const g = ctx.createGain(); g.gain.value = 0.33; o.connect(g); g.connect(filter); o.start();
      return o;
    });
    const lfo = ctx.createOscillator(); lfo.frequency.value = 0.06;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 260;
    lfo.connect(lfoGain); lfoGain.connect(filter.frequency); lfo.start();
    master.gain.linearRampToValueAtTime(0.045, ctx.currentTime + 2.5);
    audioRef.current = {
      stop: () => {
        try { master.gain.cancelScheduledValues(ctx.currentTime); master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4); } catch (e) {}
        setTimeout(() => { try { oscs.forEach((o) => o.stop()); lfo.stop(); ctx.close(); } catch (e) {} }, 500);
      },
    };
    return () => { if (audioRef.current) { try { audioRef.current.stop(); } catch (e) {} audioRef.current = null; } };
  }, [music]);

  if (!slides.length) return null;
  return (
    <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black aspect-video group shadow-2xl shadow-black/40" data-testid="highlight-reel">
      {slides.map((s, idx) => (
        <div key={idx} className="absolute inset-0" style={{ opacity: idx === i ? 1 : 0, transition: "opacity 1000ms ease-in-out" }}>
          <img src={fileUrl(s.url)} alt={s.caption} className="w-full h-full object-cover"
            style={{ transform: idx === i ? "scale(1.12)" : "scale(1)", transition: `transform ${DUR + 400}ms ease-out` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/10" />
          <div className="absolute left-0 right-0 bottom-0 p-6 md:p-9">
            <p className={`${t.text} text-xs font-semibold uppercase tracking-[0.2em] mb-2`}>{title} · {String(idx + 1).padStart(2, "0")}/{String(slides.length).padStart(2, "0")}</p>
            {s.caption && <p className="text-white font-heading font-extrabold text-xl md:text-3xl leading-tight max-w-2xl drop-shadow">{s.caption}</p>}
          </div>
        </div>
      ))}
      <div className="absolute top-3 left-3 right-24 flex gap-1.5">
        {slides.map((_, idx) => (
          <div key={idx} className="h-1 rounded-full bg-white/25 flex-1 overflow-hidden">
            <div key={`${idx}-${idx === i ? i : "x"}`} className={`h-full ${t.bg}`}
              style={idx < i ? { width: "100%" } : idx === i ? { animation: `reelfill ${DUR}ms linear forwards`, animationPlayState: playing ? "running" : "paused" } : { width: "0%" }} />
          </div>
        ))}
      </div>
      <div className="absolute top-2.5 right-3 flex gap-1.5">
        <button onClick={() => setMusic((m) => !m)} data-testid="reel-music" title={music ? "Mute soundtrack" : "Play ambient soundtrack"}
          className={`w-9 h-9 rounded-full backdrop-blur grid place-items-center text-white transition-colors ${music ? `${t.bg} ${t.btnText}` : "bg-black/50 hover:bg-black/70"}`}>
          {music ? <Music size={15} /> : <Music2 size={15} />}
        </button>
        <button onClick={() => setNarrate((n) => !n)} data-testid="reel-narrate" title={narrate ? "Mute narration" : "Play narration"}
          className={`w-9 h-9 rounded-full backdrop-blur grid place-items-center text-white transition-colors ${narrate ? `${t.bg} ${t.btnText}` : "bg-black/50 hover:bg-black/70"}`}>
          {narrate ? <Volume2 size={15} /> : <VolumeX size={15} />}
        </button>
        <button onClick={() => setPlaying((p) => !p)} data-testid="reel-playpause"
          className="w-9 h-9 rounded-full bg-black/50 backdrop-blur grid place-items-center text-white hover:bg-black/70 transition-colors">
          {playing ? <Pause size={15} /> : <Play size={15} />}
        </button>
      </div>
      <div className="absolute bottom-3 right-3 flex gap-1.5">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`} data-testid={`hl-dot-${idx}`}
            className={`h-1.5 rounded-full transition-all ${idx === i ? `w-6 ${t.bg}` : "w-2 bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}

function SolutionReel({ slides, t }) {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  useEffect(() => {
    if (!playing || slides.length < 2) return;
    const id = setTimeout(() => setI((x) => (x + 1) % slides.length), 5000);
    return () => clearTimeout(id);
  }, [playing, slides.length, i]);
  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video group" data-testid="solution-reel">
      {slides.map((s, idx) => (
        <div key={idx} className="absolute inset-0" style={{ opacity: idx === i ? 1 : 0, transition: "opacity 900ms ease-in-out" }}>
          <img src={fileUrl(s.url)} alt="" className="w-full h-full object-cover"
            style={{ transform: idx === i ? "scale(1.08)" : "scale(1)", transition: "transform 5200ms ease-out" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
          <div className="absolute left-0 right-0 bottom-0 p-5 md:p-8">
            <p className={`${t.text} text-xs font-semibold uppercase tracking-widest mb-1`}>{s.kicker || "Our solution"}</p>
            <p className="text-white font-heading font-extrabold text-xl md:text-3xl leading-tight max-w-xl">{s.title}</p>
            {s.sub && <p className="text-slate-200 text-sm md:text-base mt-2 max-w-lg">{s.sub}</p>}
          </div>
        </div>
      ))}
      <div className="absolute top-3 left-3 right-14 flex gap-1.5">
        {slides.map((_, idx) => (
          <div key={idx} className="h-1 rounded-full bg-white/25 flex-1 overflow-hidden">
            <div key={`${idx}-${idx === i ? i : "x"}`} className={`h-full ${t.bg}`}
              style={idx < i ? { width: "100%" } : idx === i ? { animation: "reelfill 5000ms linear forwards", animationPlayState: playing ? "running" : "paused" } : { width: "0%" }} />
          </div>
        ))}
      </div>
      <button onClick={() => setPlaying((p) => !p)} data-testid="reel-playpause"
        className="absolute top-2.5 right-3 w-9 h-9 rounded-full bg-black/50 backdrop-blur grid place-items-center text-white hover:bg-black/70 transition-colors">
        {playing ? <Pause size={15} /> : <Play size={15} />}
      </button>
      <div className="absolute bottom-3 right-3 flex gap-1.5">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`} data-testid={`reel-dot-${idx}`}
            className={`h-1.5 rounded-full transition-all ${idx === i ? `w-6 ${t.bg}` : "w-2 bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}

function Section({ id, title, kicker, children, t }) {
  return (
    <section id={id} className="max-w-6xl mx-auto px-5 py-14 md:py-20">
      <p className={`${t.text} font-semibold text-sm uppercase tracking-widest`}>{kicker}</p>
      <h2 className="text-3xl md:text-4xl font-heading font-extrabold mt-2 mb-8">{title}</h2>
      {children}
    </section>
  );
}
function Card({ icon: Icon, title, children, t }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <div className={`w-11 h-11 rounded-xl grid place-items-center mb-4 ${t.soft}`}><Icon size={20} /></div>
      <h3 className="text-xl font-heading font-bold mb-2">{title}</h3>
      <p className="text-slate-300 leading-relaxed">{children}</p>
    </div>
  );
}
function ContactCard({ icon: Icon, label, value, t, href }) {
  const inner = (
    <>
      <div className={`w-10 h-10 rounded-xl ${t.soft} grid place-items-center mb-3`}><Icon size={18} /></div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="text-slate-200 mt-0.5 break-words">{value}</p>
    </>
  );
  if (href) {
    const external = href.startsWith("http");
    return (
      <a href={href} target={external ? "_blank" : undefined} rel="noopener noreferrer"
        onClick={(e) => { if (external) { e.preventDefault(); window.open(href, "_blank", "noopener,noreferrer"); } }}
        data-testid={`contact-${label.toLowerCase()}`}
        className="block rounded-2xl bg-white/5 border border-white/10 p-5 transition-colors hover:bg-white/10 hover:border-white/20 cursor-pointer">
        {inner}
      </a>
    );
  }
  return <div className="rounded-2xl bg-white/5 border border-white/10 p-5">{inner}</div>;
}

function DataTable({ table, t }) {
  const hi = table.highlight;
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10 mt-2">
      <table className="w-full text-sm min-w-[560px]">
        <thead>
          <tr>
            {table.headers.map((h, i) => (
              <th key={i} className={`text-left px-4 py-3 font-bold ${i === hi ? `${t.bg} ${t.btnText}` : i === 0 ? "text-slate-100 bg-white/5" : `${t.text} bg-white/5`}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors">
              {row.map((c, ci) => (
                <td key={ci} className={`px-4 py-2.5 ${ci === hi ? `${t.soft} font-semibold` : ci === 0 ? "text-slate-100 font-medium" : "text-slate-400"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DynSection({ s, t }) {
  return (
    <Section id={s.id} t={t} title={s.title} kicker={s.kicker || ""}>
      {(s.paragraphs || []).map((p, i) => (
        <p key={i} className="text-slate-300 leading-relaxed mb-4 max-w-3xl">{p}</p>
      ))}

      {(s.groups || []).length > 0 && (
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          {s.groups.map((g, i) => (
            <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <h3 className="font-bold mb-3 flex items-center gap-2"><span className={`w-1.5 h-5 rounded ${t.bg}`} />{g.subtitle}</h3>
              <ul className="space-y-2">
                {g.bullets.map((b, j) => (
                  <li key={j} className="flex gap-2 text-sm text-slate-400 leading-relaxed"><Check size={15} className={`${t.text} mt-0.5 shrink-0`} />{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {(s.bullets || []).length > 0 && (
        s.variant === "cards" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
            {s.bullets.map((b, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-4 flex gap-3 hover:border-white/20 transition-colors">
                <div className={`w-8 h-8 rounded-lg ${t.soft} grid place-items-center shrink-0`}><Check size={16} /></div>
                <p className="text-sm text-slate-300 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-4xl mt-2">
            {s.bullets.map((b, i) => (
              <li key={i} className="flex gap-2.5 text-slate-300 text-sm leading-relaxed"><Check size={16} className={`${t.text} mt-0.5 shrink-0`} />{b}</li>
            ))}
          </ul>
        )
      )}

      {s.table && <DataTable table={s.table} t={t} />}

      {s.image && <img src={fileUrl(s.image)} alt="" className="mt-6 rounded-2xl border border-white/10 w-full max-h-96 object-cover" />}

      {s.note && <p className="text-sm text-slate-500 mt-3 max-w-3xl italic">{s.note}</p>}

      {s.callout && (
        <div className={`mt-6 rounded-2xl border ${t.chip} p-5`}>
          <p className="font-semibold text-slate-100">{s.callout}</p>
        </div>
      )}
    </Section>
  );
}
